import {
    DayNumber,
    RetailerSparkFilters,
    SparkProductFilters,
    UIEvent,
    VendorSparkFilters,
} from '@sparkplug/lib';

import { CalendarView } from '@app/componentLibrary/Calendar/calendar.types';

export type ScheduleType = 'one-time' | 'recurring';

export interface RecurringScheduleConfig {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek: DayNumber[];
    months: number[];
    monthDay: number;
    endType: 'never' | 'on-date' | 'after-occurrences';
    endDate?: string;
    occurrenceCount?: number;
}

export type EventDateTimeOnChange = ({
    startDateTime,
    endDateTime,
}: {
    startDateTime: string;
    endDateTime: string;
}) => void;

export interface EventFormFields {
    _id?: string;
    type?: UIEvent['type'];
    retailerHostType?: UIEvent['retailerHostType'];
    locationIds: string[];
    startTime?: string;
    endTime?: string;
    name?: string;
    description?: string;
    brandId?: string;
    retailerAccountId?: string;
    vendorAccountId?: string;
    requestNote?: string;
    clonedFromId?: string;
    scheduleType?: ScheduleType;
    recurringConfig?: RecurringScheduleConfig;
}

export interface EventProductSelectorFields {
    retailerFilters?: RetailerSparkFilters;
    vendorFilters?: VendorSparkFilters;
    posBrandIds?: string[];
    posCategoryIds?: string[];
    posProductIds?: string[];
    productFilters?: SparkProductFilters;
}

export type EventFormErrors = Record<
    | keyof Omit<
          EventFormFields,
          'requestNote' | '_id' | 'clonedFromId' | 'recurringConfig' | 'scheduleType'
      >
    | 'daysOfWeek'
    | 'endDate',
    boolean
>;

export const EVENT_NAVIGATION_QUERY_PARAMS = {
    create: 'create',
    editId: 'editId',
    clonedFromId: 'clonedFromId',
    detailId: 'detailId',
    deleteId: 'deleteId',
    quickApprovalCode: 'quickApprovalCode',
    declineId: 'declineId',
    fromDetailsModal: 'fromDetailsModal',
    retailerAccountId: 'retailerAccountId',
} as const;

export type EventView = 'calendar' | 'table';

export type EventScheduleType =
    | 'one-time'
    | 'recurring-daily'
    | 'recurring-weekly'
    | 'recurring-monthly';

export type EventSearchParamsRaw = {
    accountId: string;
    limit: string;
    offset: string;
    p: string;
    orderBy: string;
    order?: 'asc' | 'desc';
    brandId?: string;
    markets?: string;
    requestStatus?: string;
    eventScheduleStatus?: string;
    scheduleType?: string;
    search?: string;
    type?: UIEvent['type'];
    [EVENT_NAVIGATION_QUERY_PARAMS.create]?: 'true' | 'false';
    [EVENT_NAVIGATION_QUERY_PARAMS.editId]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.clonedFromId]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.detailId]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.deleteId]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.quickApprovalCode]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.declineId]?: string;
    [EVENT_NAVIGATION_QUERY_PARAMS.fromDetailsModal]?: 'true' | 'false';
    [EVENT_NAVIGATION_QUERY_PARAMS.retailerAccountId]?: string;
    eventView?: EventView;
    calendarView?: CalendarView;
    startTime?: string;
    endTime?: string;
    createStartDateTime?: string;
    createEndDateTime?: string;
};

export type EventSearchParams = Omit<
    EventSearchParamsRaw,
    'limit' | 'offset' | 'requestStatus' | 'eventScheduleStatus' | 'scheduleType'
> & {
    limit: number;
    offset: number;
    requestStatus?: ('approved' | 'pending' | 'expired' | 'declined')[];
    eventScheduleStatus?: ('active' | 'upcoming' | 'complete')[];
    scheduleType?: EventScheduleType[];
};
